import {
  AfterContentInit, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';

import { merge, Subject, Subscription } from 'rxjs';
import { JdOptionLiComponent } from './jd-option-li.component';
import { defaultFilterOption, JdOptionPipe, TFilterOption } from './jd-option.pipe';
import { JdOptionComponent } from './jd-option.component';
import { JdOptionGroupComponent } from './jd-option-group.component';
import {isNotNil} from '../core/convert';

@Component({
  selector           : '[jd-option-container]',
  preserveWhitespaces: false,
  templateUrl        : './jd-option-container.component.html',
  styles: [`
  `]
})
export class JdOptionContainerComponent implements AfterContentInit, OnDestroy {
  // tslint:disable-next-line:no-any
  private _listOfSelectedValue: any[];
  private _searchValue: string;
  isInit = false;
  isAddTagOptionDisplay = false;
  listOfAllTemplateOption: JdOptionComponent[] = [];
  optionSubscription: Subscription;
  groupSubscription: Subscription;
  listOfTagOption: JdOptionComponent[] = [];
  listOfFilterOption: JdOptionComponent[] = [];
  activatedOption: JdOptionComponent;

  @Input() notSearchResult$: Subject<{isNeedLoading: boolean, value: string}>;
  /** can not use ViewChild since it will match sub options in option group **/
  @ViewChildren(JdOptionLiComponent) listOfJdOptionLiComponent: QueryList<JdOptionLiComponent>;
  @Input() listOfJdOptionComponent: QueryList<JdOptionComponent> | any;
  @Input() listOfJdOptionGroupComponent: QueryList<JdOptionGroupComponent> | any;
  // tslint:disable-next-line:no-any
  @Output() jdListOfSelectedValueChange = new EventEmitter<any[]>();
  @Output() jdListOfTemplateOptionChange = new EventEmitter<JdOptionComponent[]>();
  @Output() jdClickOption = new EventEmitter<void>();
  @Output() jdScrollToBottom = new EventEmitter<void>();
  @Input() jdMode = 'default';
  @Input() jdServerSearch = false;
  @Input() jdFilterOption: TFilterOption = defaultFilterOption;
  @Input() jdMaxMultipleCount = Infinity;
  @Input() col = 24;
  @Input() isLoading = false;
  @Input() jdNotFoundContent: string;
  // tslint:disable-next-line:no-any
  @Input() compareWith = (o1: any, o2: any) => o1 === o2;

  @Input()
  set jdSearchValue(value: string) {
    this._searchValue = value;
    this.updateAddTagOptionDisplay();
    this.updateListOfFilterOption();
  }

  get jdSearchValue(): string {
    return this._searchValue;
  }

  @Input()
  // tslint:disable-next-line:no-any
  set jdListOfSelectedValue(value: any[]) {
    if (this._listOfSelectedValue !== value) {
      this._listOfSelectedValue = value;
      /** should clear activedOption when listOfSelectedValue change **/
      this.clearActivatedOption();
      this.refreshAllOptionStatus(false);
    }
  }

  // tslint:disable-next-line:no-any
  get jdListOfSelectedValue(): any[] {
    return this._listOfSelectedValue;
  }

  addTagOption(): void {
    if (this.jdListOfSelectedValue.length < this.jdMaxMultipleCount) {
      this.jdListOfSelectedValue = [ ...this.jdListOfSelectedValue, this.jdSearchValue ];
      this.jdListOfSelectedValueChange.emit(this.jdListOfSelectedValue);
    }
  }

  clickOption(option: JdOptionComponent, isPressEnter: boolean): void {
    this.updateSelectedOption(option, isPressEnter);
    this.jdClickOption.emit();
  }

  onKeyDownUl(e: KeyboardEvent): void {
    if ([ 38, 40, 13 ].indexOf(e.keyCode) > -1) {
      e.preventDefault();
      const activeIndex = this.listOfFilterOption.findIndex(item => item === this.activatedOption);
      if (e.keyCode === 38) {
        // arrow up
        const preIndex = activeIndex > 0 ? (activeIndex - 1) : (this.listOfFilterOption.length - 1);
        this.setActiveOption(this.listOfFilterOption[ preIndex ]);
      } else if (e.keyCode === 40) {
        // arrow down
        const nextIndex = activeIndex < this.listOfFilterOption.length - 1 ? (activeIndex + 1) : 0;
        this.setActiveOption(this.listOfFilterOption[ nextIndex ]);
      } else if (e.keyCode === 13) {
        // enter
        if (this.isTagsMode) {
          if (!this.isAddTagOptionDisplay) {
            this.clickOption(this.activatedOption, true);
          } else {
            this.addTagOption();
            this.jdClickOption.emit();
          }
        } else {
          this.clickOption(this.activatedOption, true);
        }
      }
    }
  }

  resetActiveOption(): void {
    const firstActiveOption = this.listOfAllTemplateOption.concat(this.listOfTagOption).find(item => this.compareWith(item.jdValue, this.jdListOfSelectedValue[ 0 ]));
    this.setActiveOption(firstActiveOption);
  }

  clearActivatedOption(): void {
    this.setActiveOption(null);
  }

  setActiveOption(option: JdOptionComponent, scroll: boolean = true): void {
    this.activatedOption = option;
    if (scroll) {
      this.scrollIntoView();
    }
  }

  scrollIntoView(): void {
    if (this.listOfJdOptionLiComponent && this.listOfJdOptionLiComponent.length) {
      const targetOption = this.listOfJdOptionLiComponent.find(o => o.jdOption === this.activatedOption);
      /* tslint:disable-next-line:no-string-literal */
      if (targetOption && targetOption.el && targetOption.el[ 'scrollIntoViewIfNeeded' ]) {
        /* tslint:disable-next-line:no-string-literal */
        setTimeout(() => targetOption.el[ 'scrollIntoViewIfNeeded' ](false), 150);
      }
    }
  }

  updateSelectedOption(option: JdOptionComponent, isPressEnter: boolean): void {
    /** update listOfSelectedOption -> update jdListOfSelectedValue -> emit jdListOfSelectedValueChange **/
    if (option && !option.jdDisabled) {
      let changed = false;
      this.setActiveOption(option);
      let listOfSelectedValue = [ ...this.jdListOfSelectedValue ];
      if (this.isMultipleOrTags) {
        const targetValue = listOfSelectedValue.find(o => this.compareWith(o, option.jdValue));
        if (isNotNil(targetValue)) {
          if (!isPressEnter) {
            /** should not toggle option when press enter **/
            listOfSelectedValue.splice(listOfSelectedValue.indexOf(targetValue), 1);
            changed = true;
          }
        } else if (this.jdListOfSelectedValue.length < this.jdMaxMultipleCount) {
          listOfSelectedValue.push(option.jdValue);
          changed = true;
        }
      } else if (!this.compareWith(listOfSelectedValue[ 0 ], option.jdValue)) {
        listOfSelectedValue = [ option.jdValue ];
        changed = true;
      }
      /** update selectedValues when click option **/
      if (changed) {
        this._listOfSelectedValue = listOfSelectedValue;
        this.jdListOfSelectedValueChange.emit(this.jdListOfSelectedValue);
        if (this.isTagsMode) {
          this.refreshAllOptionStatus(false);
        }
      }
    }
  }

  refreshListOfTagOption(): void {
    if (this.isTagsMode) {
      /** refresh tags option **/
      const listOfTagsOption = [];
      this.jdListOfSelectedValue.forEach(value => {
        const existedOption = this.listOfAllTemplateOption.find(o => this.compareWith(o.jdValue, value));
        if (!existedOption) {
          const jdOptionComponent = new JdOptionComponent();
          jdOptionComponent.jdValue = value;
          jdOptionComponent.jdLabel = value;
          listOfTagsOption.push(jdOptionComponent);
        }
      });
      this.listOfTagOption = listOfTagsOption;
    }

  }

  refreshListOfAllTemplateOption(): void {
    this.listOfAllTemplateOption = this.listOfJdOptionComponent.toArray().concat(this.listOfJdOptionGroupComponent.toArray().reduce((pre, cur) => [ ...pre, ...cur.listOfJdOptionComponent.toArray() ], []));
    Promise.resolve().then(() => this.jdListOfTemplateOptionChange.emit(this.listOfAllTemplateOption));
  }

  refreshAllOptionStatus(isTemplateOptionChange: boolean): void {
    /** update jdListOfSelectedValue | update option list -> update listOfAllTemplateOption -> update listOfSelectedOption -> update activatedOption **/
    if (this.isInit) {
      if (isTemplateOptionChange) {
        this.refreshListOfAllTemplateOption();
      }
      this.refreshListOfTagOption();
      this.updateListOfFilterOption();
      this.updateAddTagOptionDisplay();
    }
  }

  updateListOfFilterOption(): void {
    this.listOfFilterOption = new JdOptionPipe().transform(this.listOfAllTemplateOption.concat(this.listOfTagOption), this.jdSearchValue, this.jdFilterOption, this.jdServerSearch);
    if (this.jdSearchValue) {
      this.setActiveOption(this.listOfFilterOption[ 0 ]);
    }
  }

  /** watch options change in option group **/
  watchSubOptionChanges(): void {
    this.unsubscribeOption();
    let optionChanges$ = merge(
      new Subject().asObservable(),
      this.listOfJdOptionGroupComponent.changes,
      this.listOfJdOptionComponent.changes
    );
    if (this.listOfJdOptionGroupComponent.length) {
      this.listOfJdOptionGroupComponent.forEach(group => optionChanges$ = group.listOfJdOptionComponent ? merge(group.listOfJdOptionComponent.changes, optionChanges$) : optionChanges$);
    }
    this.optionSubscription = optionChanges$.subscribe(() => this.refreshAllOptionStatus(true));
  }

  unsubscribeGroup(): void {
    if (this.groupSubscription) {
      this.groupSubscription.unsubscribe();
      this.groupSubscription = null;
    }
  }

  unsubscribeOption(): void {
    if (this.optionSubscription) {
      this.optionSubscription.unsubscribe();
      this.optionSubscription = null;
    }
  }

  get isTagsMode(): boolean {
    return this.jdMode === 'tags';
  }

  get isMultipleOrTags(): boolean {
    return this.jdMode === 'tags' || this.jdMode === 'multiple';
  }

  get isNotFoundDisplay(): boolean {
    return (!this.isTagsMode) && (!this.listOfFilterOption.length);
  }

  updateAddTagOptionDisplay(): void {
    const listOfAllOption = this.listOfAllTemplateOption.concat(this.listOfTagOption).map(item => item.jdLabel);
    const isMatch = listOfAllOption.indexOf(this.jdSearchValue) > -1;
    this.isAddTagOptionDisplay = this.isTagsMode && this.jdSearchValue && (!isMatch);
  }

  dropDownScroll(e: MouseEvent, ul: HTMLUListElement): void {
    e.preventDefault();
    e.stopPropagation();
    if (ul && (ul.scrollHeight - ul.scrollTop === ul.clientHeight)) {
      this.jdScrollToBottom.emit();
    }
  }

  ngAfterContentInit(): void {
    this.isInit = true;
    this.refreshAllOptionStatus(true);
    this.watchSubOptionChanges();
    this.groupSubscription = this.listOfJdOptionGroupComponent.changes.subscribe(() => this.watchSubOptionChanges());
  }

  constructor(private _cd: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.unsubscribeGroup();
    this.unsubscribeOption();
  }
}
